import { yupToFormErrors } from 'formik'
import type { Schema } from 'yup'
import { allowedHandoffCountries, type ReturnPortal } from '~/model'

export * from '~/helpers/dateHelpers'
export * from '~/helpers/numberHelpers'
export * from '~/helpers/successHelpers'
export * from '~/helpers/labelHelpers'

/**
 * Converts a language (`nl`) to a locale (`nl-NL`) and try to be smart about it.
 *
 * @param language the language code (`nl`), defaults to `en`
 * @param country the country code (`be`), defaults to the `language` parameter
 * @return the corresponding locale
 */
export const languageToLocale = (language = 'en', country: string = language): string =>
  `${language.toLowerCase()}-${country.toUpperCase()}`

export const insertIf = <T>(condition: boolean, ...elements: T[]) => (condition ? elements : [])

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const validateSchema = async <T extends object>(validationSchema: Schema<T>, values: any) => {
  try {
    await validationSchema.validate(values, { abortEarly: false })
    return {}
  } catch (yupError) {
    return yupToFormErrors(yupError)
  }
}

export const topLevelDomain = (): string => {
  const {
    location: { hostname },
  } = window
  const tld = hostname.split('.').slice(-1)[0]
  return tld === 'localhost' || tld === 'io' ? 'nl' : tld
}

export const arrayWithItemFirst =
  <T>(data: T[]) =>
  (first: T): T[] =>
    data.includes(first) ? [first, ...data.filter((element) => element !== first)] : data

export const getLanguageFromLocale = (locale: string): string => {
  const [language = ''] = locale.split('-')
  return language
}

export const getCountryFromBrowserLanguage = () => {
  /*
   * Getting the country from navigator.language is not reliable:
   * - the split character (-, _) is not wel defined, especially for older browsers
   * - some older browsers return the country in lowercase
   * We tried to solve for all edge cases here.
   */
  const { language: locale } = navigator
  const [, country] = locale.split(/[_-]{1}/)
  return country?.toUpperCase()
}

/**
 * Algorithm:
 * 1. If the user has a preferred browser language, use that
 * 2. Use the country set as receiver in the `returnPortal` object
 * 3. Use `defaultCountry` as a fallback
 */
export const defaultUserCountryCode = (returnPortal: ReturnPortal) => {
  const allowedCountries = allowedHandoffCountries(returnPortal) as string[]
  const userCountry = getCountryFromBrowserLanguage()
  const receiverCountry = returnPortal.address?.address?.countryCode
  const firstAllowedCountry = allowedCountries[0]

  if (userCountry && allowedCountries.includes(userCountry)) {
    return userCountry
  }
  if (receiverCountry && allowedCountries.includes(receiverCountry)) {
    return receiverCountry
  }
  return firstAllowedCountry
}
